import ArrowDownIconDark from '@Resources/assets/icons/dark/downArrowLarge.svg';
import ArrowUpIconDark from '@Resources/assets/icons/dark/upArrowLarge.svg';
import ArrowDownIconLight from '@Resources/assets/icons/light/downArrowLarge.svg';
import ArrowUpIconLight from '@Resources/assets/icons/light/upArrowLarge.svg';
import ColorsStyles from '@Styles/colors.scss';
import cx from 'classnames';
import _isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Styles from '../EntityDrawer.scss';
import EntityImageControls from './EntityImageControls';
import EntityImageThumbnail from './EntityImageThumbnail';
import EntityMainImage from './EntityMainImage';
import ControlsSkeleton from './Skeletons/ControlsSkeleton';
import MainImageSkeleton from './Skeletons/MainImageSkeleton';
import ThumbnailsSkeleton from './Skeletons/ThumbnailsSkeleton';

const EntityImages = (props) => {
    const {
        images,
        isLoading,
        entityId,
        initialImage,
        containerHeight,
        onExpand,
        onCollapse,
        hideThumbnails,
        loadedThumbnails,
        loadedMainImages,
    } = props;

    const [selectedImgIndex, setSelectedImgIndex] = useState<number | null>(
        null
    );
    const [baseId, setBaseId] = useState('');
    const [page, setPage] = useState(1);
    const [thumbnailAmount, setThumbnailAmount] = useState<number | null>(null);
    const mainImgRef: any = useRef();
    const thumbnailContainerId = useMemo(
        () => `thumbnailCont${entityId}`,
        [entityId]
    );
    const [upArrowHovering, setUpArrowHovering] = useState(false);
    const [downArrowHovering, setDownArrowHovering] = useState(false);

    useEffect(() => {
        setBaseId(entityId);
        setSelectedImgIndex(initialImage);
        setPage(1);
    }, [isLoading, entityId, initialImage]);

    useEffect(() => {
        if (!hideThumbnails) {
            const height = parseInt(
                window
                    .getComputedStyle(
                        document.getElementById(thumbnailContainerId)
                    )
                    .height.split('px')[0]
            );

            setThumbnailAmount(Math.floor(height / 91));
        }
    }, [hideThumbnails, containerHeight, thumbnailContainerId]);

    const pages = useMemo(() => {
        if (hideThumbnails) {
            return null;
        }

        if (isLoading || _isNil(thumbnailAmount)) {
            return null;
        }
        return Math.ceil(images.length / thumbnailAmount);
    }, [hideThumbnails, isLoading, images, thumbnailAmount]);

    const pageRange = useMemo(() => {
        if (isLoading || _isNil(thumbnailAmount)) {
            return null;
        }
        return {
            min: (page - 1) * thumbnailAmount,
            max: (page - 1) * thumbnailAmount + thumbnailAmount - 1,
        };
    }, [isLoading, page, thumbnailAmount]);

    const trySetImage = (index) => {
        if (index > -1 && index < images.length) {
            setSelectedImgIndex(index);
            mainImgRef.current.reset();
            if (index < pageRange.min && page - 1 > 0) {
                setPage(page - 1);
            } else if (index > pageRange.max && page + 1 < pages) {
                setPage(page + 1);
            }
        }
    };

    const trySetPage = (newPage) => {
        if (newPage > 0 && newPage <= pages) {
            setPage(newPage);
        }
    };

    const thumbnails = useMemo(() => {
        if (hideThumbnails) return null;

        if (isLoading || _isNil(thumbnailAmount)) {
            return <ThumbnailsSkeleton getMany />;
        }

        return images.map((image, i) => {
            const isVisible = i >= pageRange.min && i <= pageRange.max;
            if (!isVisible) return null;

            const loadedUrl = !_isNil(loadedThumbnails[i])
                ? loadedThumbnails[i]
                : undefined;

            return (
                <EntityImageThumbnail
                    id={`thumbnail-${image.id}`}
                    key={`thmbnl${i}`}
                    loadedUrl={loadedUrl}
                    isSelected={selectedImgIndex === i}
                    onClick={() => trySetImage(i)}
                    isVisible={isVisible}
                />
            );
        });
    }, [
        hideThumbnails,
        images,
        isLoading,
        selectedImgIndex,
        pageRange,
        thumbnailAmount,
        loadedThumbnails,
    ]);

    const controls = useMemo(() => {
        if (isLoading) {
            return <ControlsSkeleton />;
        }

        return (
            <EntityImageControls
                onZoomIn={() => mainImgRef.current.zoomIn()}
                onZoomOut={() => mainImgRef.current.zoomOut()}
                onResetZoom={() => mainImgRef.current.resetZoom()}
                onNext={() => trySetImage(selectedImgIndex + 1)}
                onPrev={() => trySetImage(selectedImgIndex - 1)}
                hideSelectControls={images.length === 1}
                onExpand={onExpand}
                onCollapse={onCollapse}
                selectedImgIndex={selectedImgIndex}
            />
        );
    }, [isLoading, selectedImgIndex]);

    const mainImage = useMemo(() => {
        if (
            isLoading ||
            _isNil(selectedImgIndex) ||
            _isNil(loadedMainImages[selectedImgIndex])
        ) {
            return <MainImageSkeleton containerHeight={containerHeight} />;
        }

        const id = `${baseId}-Main`;

        return (
            <EntityMainImage
                ref={mainImgRef}
                id={id}
                key={id}
                containerHeight={containerHeight}
                loadedUrl={loadedMainImages[selectedImgIndex]}
            />
        );
    }, [images, loadedMainImages, isLoading, selectedImgIndex, baseId]);

    const upArrowIcon =
        pages > 1 && upArrowHovering ? (
            <ArrowUpIconLight />
        ) : (
            <ArrowUpIconDark />
        );

    const downArrowIcon =
        pages > 1 && downArrowHovering ? (
            <ArrowDownIconLight />
        ) : (
            <ArrowDownIconDark />
        );

    return (
        <div
            className={Styles.entityDrawerImageContainer}
            style={{ minHeight: containerHeight, maxHeight: containerHeight }}
        >
            <div className={Styles.entityDrawerImageMainContainer}>
                {!hideThumbnails && (
                    <div className={Styles.entityDrawerImageThumbnailContainer}>
                        <div
                            className={Styles.entityDrawerThumbnails}
                            id={thumbnailContainerId}
                        >
                            {thumbnails}
                        </div>
                        <div>
                            <button
                                id="thumbnails-up-arrow-btn"
                                className={cx(
                                    Styles.changePageBtn,
                                    pages > 1
                                        ? Styles.changePageBtnActive
                                        : Styles.changePageBtnDisabled
                                )}
                                style={{
                                    marginBottom: '4px',
                                    backgroundColor:
                                        upArrowHovering && pages > 1
                                            ? ColorsStyles.ikeGradient1
                                            : ColorsStyles.ikeGradient12,
                                }}
                                onClick={() => trySetPage(page - 1)}
                                onMouseOver={() => setUpArrowHovering(true)}
                                onMouseLeave={() => setUpArrowHovering(false)}
                            >
                                {upArrowIcon}
                            </button>
                            <button
                                id="thumbnails-down-arrow-btn"
                                className={cx(
                                    Styles.changePageBtn,
                                    pages > 1
                                        ? Styles.changePageBtnActive
                                        : Styles.changePageBtnDisabled
                                )}
                                onClick={() => trySetPage(page + 1)}
                                style={{
                                    backgroundColor:
                                        downArrowHovering && pages > 1
                                            ? ColorsStyles.ikeGradient1
                                            : ColorsStyles.ikeGradient12,
                                }}
                                onMouseOver={() => setDownArrowHovering(true)}
                                onMouseLeave={() => setDownArrowHovering(false)}
                            >
                                {downArrowIcon}
                            </button>
                        </div>
                    </div>
                )}
                <div className={Styles.entityDrawerImageImageContainer}>
                    {mainImage}
                </div>
            </div>
            <div className={Styles.entityDrawerImageControlsContainer}>
                {!hideThumbnails && (
                    <div
                        className={Styles.entityDrawerImageThumbnailContainer}
                    />
                )}

                <div
                    className={
                        Styles.entityDrawerImageControlsControlsContainer
                    }
                >
                    {controls}
                </div>
            </div>
        </div>
    );
};

EntityImages.propTypes = {
    images: PropTypes.array,
    isLoading: PropTypes.bool,
    entityId: PropTypes.string,
    initialImage: PropTypes.number,
    containerHeight: PropTypes.string,
    onExpand: PropTypes.func,
    onCollapse: PropTypes.func,
    hideThumbnails: PropTypes.bool,
    loadedThumbnails: PropTypes.array,
    loadedMainImages: PropTypes.array,
};
EntityImages.defaultProps = {
    initialImage: 0,
    containerHeight: Styles.defaultImgContainerHeight,
    hideThumbnails: false,
};
export default EntityImages;
